import * as THREE from 'three';
import { BinaryInstancedSurface } from '../../rendering/binary/BinaryInstancedSurface.js';

/** Minimum glyph quads across the narrow side of a sidewalk strip. */
const MIN_GLYPHS_ACROSS = 10;

/**
 * Narrow binary sidewalk strip along a road edge (dense glyphs + curb lip).
 */
export class Sidewalk {
  /**
   * @param {{
   *   material: import('../../rendering/binary/BinaryMaterial.js').BinaryMaterial;
   *   centerX: number;
   *   centerZ: number;
   *   width: number;
   *   depth: number;
   *   cellsPerMeter: number;
   *   seed: number;
   *   cellIdBase: number;
   * }} options
   */
  constructor({
    material,
    centerX,
    centerZ,
    width,
    depth,
    cellsPerMeter,
    seed,
    cellIdBase,
  }) {
    this.group = new THREE.Group();
    this.group.name = 'Sidewalk';

    const shortSide = Math.min(width, depth);
    const density = Math.max(
      cellsPerMeter + 2,
      MIN_GLYPHS_ACROSS / Math.max(shortSide, 0.5),
    );

    let cellNext = cellIdBase;

    this.surface = new BinaryInstancedSurface({
      material,
      width,
      height: depth,
      cellsPerMeter: density,
      center: new THREE.Vector3(centerX, 0.078, centerZ),
      normal: new THREE.Vector3(0, 1, 0),
      tangentU: new THREE.Vector3(1, 0, 0),
      tangentV: new THREE.Vector3(0, 0, -1),
      seed,
      cellIdOffset: cellNext,
    });
    cellNext += this.surface.instanceCount;
    this.surface.mesh.renderOrder = 3;
    this.group.add(this.surface.mesh);

    const curbWidth = Math.min(0.28, shortSide * 0.22);
    const isRoadStrip = shortSide <= 6;

    if (isRoadStrip) {
      const curbDepth = depth;
      const curbW = width;
      const alongX = width >= depth;
      const curbCenter = new THREE.Vector3(centerX, 0.072, centerZ);
      if (alongX) {
        curbCenter.x += width * 0.5 - curbWidth * 0.5;
      } else {
        curbCenter.z += depth * 0.5 - curbWidth * 0.5;
      }

      this.curb = new BinaryInstancedSurface({
        material,
        width: alongX ? curbWidth : curbW,
        height: alongX ? curbDepth : curbWidth,
        cellsPerMeter: Math.max(density, 12),
        center: curbCenter,
        normal: new THREE.Vector3(0, 1, 0),
        tangentU: new THREE.Vector3(1, 0, 0),
        tangentV: new THREE.Vector3(0, 0, -1),
        seed: seed + 913,
        cellIdOffset: cellNext,
      });
      cellNext += this.curb.instanceCount;
      this.curb.mesh.renderOrder = 4;
      this.group.add(this.curb.mesh);
    } else {
      this.curb = null;
    }

    this.cellsUsed = cellNext - cellIdBase;
    this.maxColumns = Math.max(
      this.surface.columns,
      this.curb?.columns ?? 0,
    );
  }

  dispose() {
    this.surface.dispose();
    this.curb?.dispose();
    this.group.clear();
  }
}
